/**
 * PPLB (Argox Printer Language B / Eltron EPL2 uyumlu) komut üreticisi.
 *
 * PPLA'dan iki önemli farkı var:
 *
 *  1) **Tek adımlı grafik.** PPLA'da görüntü önce yazıcı belleğine indirilip
 *     sonra etiket formatında konumlandırılıyor; PPLB'de `GW` komutu ham
 *     rasteri doğrudan görüntü tamponuna yazıyor. Yazıcı belleğinde iz
 *     kalmadığı için isim çakışması ve `Q` ile temizlik derdi yok.
 *  2) **Origin sol ÜST köşe.** PPLA'da sol alttı ve ekran koordinatından
 *     çevirmek gerekiyordu; burada ekran mantığıyla aynı.
 *
 * `GW` veri düzeni (Argox PPLB ve Eltron EPL2 kılavuzları aynı şeyi söylüyor):
 *   - satır satır, sıkıştırma yok, **üstten aşağı**
 *   - her byte 8 dot; **en anlamlı bit en soldaki dot**
 *   - **bit 0 = siyah, bit 1 = boş** (PPLA ile aynı polarite)
 *   - satır genişliği byte cinsinden verildiği için 8 dot katına hizalanmalı
 *     (PPLA'da BMP yüzünden 32 gerekiyordu)
 *
 * Ham veri içinde satır sonu byte'ı geçebilir; yazıcı okuyacağı byte sayısını
 * `p3 × p4` ile hesapladığı için bu sorun olmuyor — PPLA'daki `<SOH>D`
 * (interaction komutlarını kapatma) adımına burada gerek yok.
 */

import { MonoBitmap } from './bmp1bit';
import { ascii, clamp, concat } from './byteUtils';

const LF = '\n';

export interface EplJobOptions {
  /** Basılacak kopya sayısı (1-9999) */
  copies?: number;
  /** Koyuluk 0-15. Verilmezse yazıcının kendi ayarı kullanılır. */
  darkness?: number;
  /** Baskı hızı (model bağımlı, tipik 1-6). Verilmezse yazıcı ayarı. */
  speed?: number;
  /** Etiketin sol kenarından uzaklık (dot) */
  offsetXDots?: number;
  /** Etiketin üst kenarından uzaklık (dot) */
  offsetYDots?: number;
  /** Yazdırma genişliği (dot). Verilmezse yazıcının tam kafa genişliği kullanılır. */
  labelWidthDots?: number;
  /**
   * Etiket uzunluğu ve gap (dot). İkisi birlikte verilmelidir.
   * Verilmezse yazıcının AutoSense ölçümü korunur — yanlış bir gap değeri
   * etiketleri kaydırdığı için varsayılan olarak gönderilmiyor.
   */
  labelLengthDots?: number;
  gapDots?: number;
}

export interface EplRasterResult {
  bytes: Uint8Array;
  /** Satır başına byte (GW p3 parametresi) */
  bytesPerRow: number;
  /** Satır sayısı (GW p4 parametresi) */
  rows: number;
}

/** Mürekkep matrisini `GW` komutunun beklediği ham byte dizisine çevirir */
export function encodeEplRaster(mono: MonoBitmap): EplRasterResult {
  const { width, height, ink } = mono;
  if (width <= 0 || height <= 0) {
    throw new Error('Geçersiz bitmap ölçüsü');
  }

  const bytesPerRow = Math.ceil(width / 8);
  const bytes = new Uint8Array(bytesPerRow * height);
  // Boş alan 1'lerle dolu başlar, mürekkep bitleri sonra silinir
  bytes.fill(0xff);

  for (let y = 0; y < height; y++) {
    const rowBase = y * bytesPerRow;
    const srcBase = y * width;
    for (let x = 0; x < width; x++) {
      if (ink[srcBase + x] === 0) continue;
      bytes[rowBase + (x >> 3)] &= ~(1 << (7 - (x & 7))) & 0xff;
    }
  }

  return { bytes, bytesPerRow, rows: height };
}

/** Tek bir bitmap etiketi için tam PPLB iş akışını üretir */
export function buildEplBitmapJob(mono: MonoBitmap, options?: EplJobOptions): Uint8Array {
  const raster = encodeEplRaster(mono);
  const copies = clamp(options?.copies ?? 1, 1, 9999);

  const chunks: Uint8Array[] = [];
  const push = (s: string) => chunks.push(ascii(s));

  // Görüntü tamponunu temizle. Ölçü komutları (q/Q) tamponu da sıfırladığı
  // için grafikten ÖNCE gönderilmek zorunda.
  push(`N${LF}`);

  if (options?.labelWidthDots) {
    push(`q${Math.round(options.labelWidthDots)}${LF}`);
  }
  if (options?.labelLengthDots && options?.gapDots !== undefined) {
    push(`Q${Math.round(options.labelLengthDots)},${Math.round(options.gapDots)}${LF}`);
  }
  if (options?.speed !== undefined) {
    push(`S${clamp(options.speed, 1, 6)}${LF}`);
  }
  if (options?.darkness !== undefined) {
    push(`D${clamp(options.darkness, 0, 15)}${LF}`);
  }

  // Besleme yönü yazıcı ayarına bırakılmıyor; çıktının hangi kenardan
  // başlayacağı deterministik olmalı
  push(`ZT${LF}`);

  const x = Math.max(0, Math.round(options?.offsetXDots ?? 0));
  const y = Math.max(0, Math.round(options?.offsetYDots ?? 0));
  push(`GW${x},${y},${raster.bytesPerRow},${raster.rows},`);
  chunks.push(raster.bytes);
  push(LF);

  push(`P${copies}${LF}`);

  return concat(chunks);
}

/** Yazıcıyı bilinen bir duruma getirir — sıkışma sonrası kurtarma için */
export function buildEplReset(): Uint8Array {
  return ascii(`N${LF}`);
}
